import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calculator as CalcIcon, IndianRupee, TrendingUp, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Calculators = () => {
  const navigate = useNavigate();
  
  // Income Tax Calculator State
  const [taxInput, setTaxInput] = useState({
    annual_income: '',
    age_group: 'below_60',
    regime: 'new',
    deductions_80c: '0',
    deductions_80d: '0',
    home_loan_interest: '0',
    nps_additional: '0'
  });
  const [taxResult, setTaxResult] = useState(null);
  const [taxLoading, setTaxLoading] = useState(false);

  // GST Calculator State
  const [gstInput, setGstInput] = useState({
    amount: '',
    gst_rate: '18',
    calculation_type: 'exclusive',
    transaction_type: 'intrastate'
  });
  const [gstResult, setGstResult] = useState(null);
  const [gstLoading, setGstLoading] = useState(false);

  // EMI Calculator State
  const [emiInput, setEmiInput] = useState({
    loan_amount: '',
    interest_rate: '',
    tenure_months: ''
  });
  const [emiResult, setEmiResult] = useState(null);
  const [emiLoading, setEmiLoading] = useState(false);

  // Income Tax Calculator
  const calculateIncomeTax = async () => {
    setTaxLoading(true);
    try {
      const response = await axios.post(`${API}/calculators/income-tax`, {
        ...taxInput,
        annual_income: parseFloat(taxInput.annual_income) || 0,
        deductions_80c: parseFloat(taxInput.deductions_80c) || 0,
        deductions_80d: parseFloat(taxInput.deductions_80d) || 0,
        home_loan_interest: parseFloat(taxInput.home_loan_interest) || 0,
        nps_additional: parseFloat(taxInput.nps_additional) || 0
      });
      setTaxResult(response.data);
    } catch (error) {
      console.error('Tax calculation error:', error);
      alert('Error calculating tax. Please check your inputs.');
    } finally {
      setTaxLoading(false);
    }
  };

  // GST Calculator
  const calculateGST = async () => {
    setGstLoading(true);
    try {
      const response = await axios.post(`${API}/calculators/gst`, {
        ...gstInput,
        amount: parseFloat(gstInput.amount) || 0,
        gst_rate: parseFloat(gstInput.gst_rate) || 0
      });
      setGstResult(response.data);
    } catch (error) {
      console.error('GST calculation error:', error);
      alert('Error calculating GST. Please check your inputs.');
    } finally {
      setGstLoading(false);
    }
  };

  // EMI Calculator
  const calculateEMI = async () => {
    setEmiLoading(true);
    try {
      const response = await axios.post(`${API}/calculators/emi`, {
        loan_amount: parseFloat(emiInput.loan_amount) || 0,
        interest_rate: parseFloat(emiInput.interest_rate) || 0,
        tenure_months: parseInt(emiInput.tenure_months) || 0
      });
      setEmiResult(response.data);
    } catch (error) {
      console.error('EMI calculation error:', error);
      alert('Error calculating EMI. Please check your inputs.');
    } finally {
      setEmiLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Home
            </Button>
            <div className="flex items-center gap-2">
              <CalcIcon className="w-6 h-6 text-teal-600" />
              <h1 className="text-xl font-bold text-gray-900">Financial Calculators</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Free Financial Calculators</h2>
            <p className="text-lg text-gray-600">
              Quick and accurate calculations for your financial planning
            </p>
          </div>

          <Tabs defaultValue="income-tax" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-8">
              <TabsTrigger value="income-tax">Income Tax</TabsTrigger>
              <TabsTrigger value="gst">GST</TabsTrigger>
              <TabsTrigger value="emi">EMI</TabsTrigger>
            </TabsList>

            {/* Income Tax Calculator */}
            <TabsContent value="income-tax">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <IndianRupee className="w-5 h-5 text-teal-600" />
                    Income Tax Calculator (FY 2025-26)
                  </CardTitle>
                  <CardDescription>
                    Calculate your income tax liability under old and new tax regimes
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="annual_income">Annual Income (₹)</Label>
                      <Input
                        id="annual_income"
                        type="number"
                        placeholder="e.g., 1000000"
                        value={taxInput.annual_income}
                        onChange={(e) => setTaxInput({...taxInput, annual_income: e.target.value})}
                      />
                    </div>

                    <div>
                      <Label htmlFor="age_group">Age Group</Label>
                      <Select value={taxInput.age_group} onValueChange={(value) => setTaxInput({...taxInput, age_group: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="below_60">Below 60 years</SelectItem>
                          <SelectItem value="60_to_80">60 to 80 years</SelectItem>
                          <SelectItem value="above_80">Above 80 years</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="regime">Tax Regime</Label>
                      <Select value={taxInput.regime} onValueChange={(value) => setTaxInput({...taxInput, regime: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New Regime</SelectItem>
                          <SelectItem value="old">Old Regime (with deductions)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {taxInput.regime === 'old' && (
                      <>
                        <div>
                          <Label htmlFor="deductions_80c">80C Deductions (₹)</Label>
                          <Input
                            id="deductions_80c"
                            type="number"
                            placeholder="Max 150000"
                            value={taxInput.deductions_80c}
                            onChange={(e) => setTaxInput({...taxInput, deductions_80c: e.target.value})}
                          />
                        </div>

                        <div>
                          <Label htmlFor="deductions_80d">80D Health Insurance (₹)</Label>
                          <Input
                            id="deductions_80d"
                            type="number"
                            placeholder="Max 25000/50000"
                            value={taxInput.deductions_80d}
                            onChange={(e) => setTaxInput({...taxInput, deductions_80d: e.target.value})}
                          />
                        </div>

                        <div>
                          <Label htmlFor="home_loan_interest">Home Loan Interest (₹)</Label>
                          <Input
                            id="home_loan_interest"
                            type="number"
                            placeholder="Max 200000"
                            value={taxInput.home_loan_interest}
                            onChange={(e) => setTaxInput({...taxInput, home_loan_interest: e.target.value})}
                          />
                        </div>

                        <div>
                          <Label htmlFor="nps_additional">NPS 80CCD(1B) (₹)</Label>
                          <Input
                            id="nps_additional"
                            type="number"
                            placeholder="Max 50000"
                            value={taxInput.nps_additional}
                            onChange={(e) => setTaxInput({...taxInput, nps_additional: e.target.value})}
                          />
                        </div>
                      </>
                    )}
                  </div>

                  <Button 
                    onClick={calculateIncomeTax} 
                    disabled={taxLoading || !taxInput.annual_income}
                    className="w-full bg-teal-600 hover:bg-teal-700"
                  >
                    {taxLoading ? 'Calculating...' : 'Calculate Tax'}
                  </Button>

                  {taxResult && (
                    <div className="mt-6 p-6 bg-teal-50 border-2 border-teal-200 rounded-xl">
                      <h3 className="text-xl font-bold text-gray-900 mb-4">Tax Calculation Result</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-white p-4 rounded-lg">
                          <p className="text-sm text-gray-600">Taxable Income</p>
                          <p className="text-2xl font-bold text-gray-900">{formatCurrency(taxResult.taxable_income)}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg">
                          <p className="text-sm text-gray-600">Total Tax (incl. cess)</p>
                          <p className="text-2xl font-bold text-teal-600">{formatCurrency(taxResult.total_tax_with_cess)}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg">
                          <p className="text-sm text-gray-600">Effective Tax Rate</p>
                          <p className="text-2xl font-bold text-gray-900">{taxResult.effective_tax_rate}%</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg">
                          <p className="text-sm text-gray-600">Regime Selected</p>
                          <p className="text-2xl font-bold text-gray-900 capitalize">{taxResult.regime}</p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 mt-4">
                        * This is an estimate. Consult CA Ravi Sahu for accurate tax planning.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* GST Calculator */}
            <TabsContent value="gst">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-teal-600" />
                    GST Calculator
                  </CardTitle>
                  <CardDescription>
                    Calculate GST with CGST, SGST, and IGST breakdown
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="amount">Amount (₹)</Label>
                      <Input
                        id="amount"
                        type="number"
                        placeholder="e.g., 10000"
                        value={gstInput.amount}
                        onChange={(e) => setGstInput({...gstInput, amount: e.target.value})}
                      />
                    </div>

                    <div>
                      <Label htmlFor="gst_rate">GST Rate (%)</Label>
                      <Select value={gstInput.gst_rate} onValueChange={(value) => setGstInput({...gstInput, gst_rate: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="5">5%</SelectItem>
                          <SelectItem value="12">12%</SelectItem>
                          <SelectItem value="18">18%</SelectItem>
                          <SelectItem value="28">28%</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="calculation_type">Calculation Type</Label>
                      <Select value={gstInput.calculation_type} onValueChange={(value) => setGstInput({...gstInput, calculation_type: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="exclusive">Exclusive (Add GST)</SelectItem>
                          <SelectItem value="inclusive">Inclusive (Remove GST)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="transaction_type">Transaction Type</Label>
                      <Select value={gstInput.transaction_type} onValueChange={(value) => setGstInput({...gstInput, transaction_type: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="intrastate">Intrastate (CGST + SGST)</SelectItem>
                          <SelectItem value="interstate">Interstate (IGST)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Button 
                    onClick={calculateGST} 
                    disabled={gstLoading || !gstInput.amount}
                    className="w-full bg-teal-600 hover:bg-teal-700"
                  >
                    {gstLoading ? 'Calculating...' : 'Calculate GST'}
                  </Button>

                  {gstResult && (
                    <div className="mt-6 p-6 bg-teal-50 border-2 border-teal-200 rounded-xl">
                      <h3 className="text-xl font-bold text-gray-900 mb-4">GST Breakdown</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-white p-4 rounded-lg">
                          <p className="text-sm text-gray-600">Original Amount</p>
                          <p className="text-2xl font-bold text-gray-900">{formatCurrency(gstResult.original_amount)}</p>
                        </div>
                        {gstResult.transaction_type === 'intrastate' ? (
                          <>
                            <div className="bg-white p-4 rounded-lg">
                              <p className="text-sm text-gray-600">CGST ({gstResult.gst_rate/2}%)</p>
                              <p className="text-2xl font-bold text-teal-600">{formatCurrency(gstResult.cgst)}</p>
                            </div>
                            <div className="bg-white p-4 rounded-lg">
                              <p className="text-sm text-gray-600">SGST ({gstResult.gst_rate/2}%)</p>
                              <p className="text-2xl font-bold text-teal-600">{formatCurrency(gstResult.sgst)}</p>
                            </div>
                          </>
                        ) : (
                          <div className="bg-white p-4 rounded-lg">
                            <p className="text-sm text-gray-600">IGST ({gstResult.gst_rate}%)</p>
                            <p className="text-2xl font-bold text-teal-600">{formatCurrency(gstResult.igst)}</p>
                          </div>
                        )}
                        <div className="bg-white p-4 rounded-lg">
                          <p className="text-sm text-gray-600">Total GST</p>
                          <p className="text-2xl font-bold text-teal-600">{formatCurrency(gstResult.total_gst)}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg md:col-span-2">
                          <p className="text-sm text-gray-600">Final Amount</p>
                          <p className="text-3xl font-bold text-gray-900">{formatCurrency(gstResult.final_amount)}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* EMI Calculator */}
            <TabsContent value="emi">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Home className="w-5 h-5 text-teal-600" />
                    EMI Calculator
                  </CardTitle>
                  <CardDescription>
                    Calculate your monthly EMI for home, car, or personal loans
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="loan_amount">Loan Amount (₹)</Label>
                      <Input
                        id="loan_amount"
                        type="number"
                        placeholder="e.g., 2500000"
                        value={emiInput.loan_amount}
                        onChange={(e) => setEmiInput({...emiInput, loan_amount: e.target.value})}
                      />
                    </div>

                    <div>
                      <Label htmlFor="interest_rate">Interest Rate (% per annum)</Label>
                      <Input
                        id="interest_rate"
                        type="number"
                        step="0.1"
                        placeholder="e.g., 8.5"
                        value={emiInput.interest_rate}
                        onChange={(e) => setEmiInput({...emiInput, interest_rate: e.target.value})}
                      />
                    </div>

                    <div className="md:col-span-2">
                      <Label htmlFor="tenure_months">Loan Tenure (Months)</Label>
                      <Input
                        id="tenure_months"
                        type="number"
                        placeholder="e.g., 240 (20 years)"
                        value={emiInput.tenure_months}
                        onChange={(e) => setEmiInput({...emiInput, tenure_months: e.target.value})}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Quick reference: 12 months = 1 year, 60 months = 5 years, 240 months = 20 years
                      </p>
                    </div>
                  </div>

                  <Button 
                    onClick={calculateEMI} 
                    disabled={emiLoading || !emiInput.loan_amount || !emiInput.interest_rate || !emiInput.tenure_months}
                    className="w-full bg-teal-600 hover:bg-teal-700"
                  >
                    {emiLoading ? 'Calculating...' : 'Calculate EMI'}
                  </Button>

                  {emiResult && (
                    <div className="mt-6 p-6 bg-teal-50 border-2 border-teal-200 rounded-xl">
                      <h3 className="text-xl font-bold text-gray-900 mb-4">EMI Details</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-white p-4 rounded-lg md:col-span-2">
                          <p className="text-sm text-gray-600">Monthly EMI</p>
                          <p className="text-4xl font-bold text-teal-600">{formatCurrency(emiResult.monthly_emi)}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg">
                          <p className="text-sm text-gray-600">Principal Amount</p>
                          <p className="text-2xl font-bold text-gray-900">{formatCurrency(emiResult.principal_amount)}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg">
                          <p className="text-sm text-gray-600">Total Interest</p>
                          <p className="text-2xl font-bold text-orange-600">{formatCurrency(emiResult.total_interest)}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg md:col-span-2">
                          <p className="text-sm text-gray-600">Total Payment</p>
                          <p className="text-2xl font-bold text-gray-900">{formatCurrency(emiResult.total_payment)}</p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 mt-4">
                        * EMI calculation is approximate. Actual EMI may vary based on lender's terms.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* CTA */}
          <div className="mt-12 text-center p-8 bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl border-2 border-teal-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Need Personalized Financial Advice?
            </h3>
            <p className="text-gray-700 mb-6">
              Our chartered accountants can help you with tax planning, financial management, and compliance
            </p>
            <Button
              size="lg"
              onClick={() => {
                navigate('/');
                setTimeout(() => {
                  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }}
              className="bg-teal-600 hover:bg-teal-700"
            >
              Schedule Free Consultation
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calculators;
